import { Platform } from 'react-native';
import { useAuthStore, useModalStore, useToastStore } from '@/store';

/**
 * Work mode kill switch.
 *
 * The server owns the flag (WORK_MODE_DISABLED=true → `user.features.workModeEnabled`
 * false on /json/user/me) so it can be flipped without shipping a client.
 * EXPO_PUBLIC_WORK_MODE_DISABLED=true is a build-time fallback for the web
 * bundle when the API cannot be redeployed first.
 *
 * While disabled the Work toggle stays visible but every way into Work — the
 * toggle, home intent chips, posting into an existing Work thread — shows the
 * unavailable notice instead. Web gets a toast; iOS/Android get the
 * "Work is coming soon" modal, since store review does not allow us to
 * present a feature as beta or temporarily broken there.
 */
export const WORK_MODE_DISABLED_REASON = 'work_mode_disabled';

export function isWorkModeDisabledForUser(user) {
  if (process.env.EXPO_PUBLIC_WORK_MODE_DISABLED === 'true') return true;
  return user?.features?.workModeEnabled === false;
}

export function useWorkModeDisabled() {
  return useAuthStore((s) => isWorkModeDisabledForUser(s.user));
}

export function isWorkModeDisabled() {
  return isWorkModeDisabledForUser(useAuthStore?.getState?.()?.user);
}

export function isWorkModeDisabledError(err) {
  return err?.data?.reason === WORK_MODE_DISABLED_REASON;
}

export function workModeUnavailableMessage() {
  // Resolved lazily: this module is imported by lightweight composer helpers
  // whose callers should not pay for (or need to mock) the translation tree.
  const { getTranslation } = require('@/hooks/useTranslation');
  return getTranslation('work.unavailable');
}

export function notifyWorkModeUnavailable() {
  if (Platform.OS !== 'web') {
    useModalStore.getState().actions.openModal('CoworkerComingSoonModal');
    return;
  }
  useToastStore.getState().actions.addToast(workModeUnavailableMessage(), 'info');
}

/**
 * Gate for the toggle and other "enter Work" actions. Returns true when Work
 * is disabled (and the notice has been shown), so callers can bail out.
 */
export function blockIfWorkModeDisabled() {
  if (!isWorkModeDisabled()) return false;
  notifyWorkModeUnavailable();
  return true;
}
